import { FC, useCallback, useMemo } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
} from '@mui/material';
import styled from 'styled-components';
import {
  BasketCheckoutEvent,
  CategoryViewShopperEvent,
  ProductViewShopperEvent,
  SearchShopperEvent,
  ShopperEvent,
  ShopperEventCategory,
  chatActions,
  chatSelectors,
  useAppDispatch,
  useAppSelector,
} from '@vs-online-only-app/state';
import { IconAddToCart, IconRemoveFromCart } from '@vs-online-only-app/ui';
import ShopperEventsList from './ShopperEventsList';
import { useSelectedHeroConversation } from '../../hooks/useSelectedHeroConversation';
import { differenceInCalendarDays } from 'date-fns';
import { ShopperEventsByCategory } from '../selected-conversation/SelectedConversationShopperProfile';
import { Close } from '@mui/icons-material';

const CloseButton = styled(IconButton)`
  position: absolute;
  top: ${({ theme }) => theme.spacing(1)};
  right: ${({ theme }) => theme.spacing(1)};
`;

enum EventTimeframe {
  TODAY = 'Today',
  LAST_30_DAYS = 'Last 30 days',
  LAST_YEAR = 'Last year',
  OLDER = 'Older',
}

type Props = {
  events: ShopperEventsByCategory;
};

export const ShopperEventsModal: FC<Props> = ({ events }) => {
  const dispatch = useAppDispatch();
  const conversation = useSelectedHeroConversation();
  const selectedEventsCategory = useAppSelector(
    chatSelectors.selectSelectedShopperEventCategory
  );

  const getEventTimeframe = (event: ShopperEvent) => {
    const daysDiff = differenceInCalendarDays(new Date(), new Date(event.time));
    if (daysDiff < 1) {
      return EventTimeframe.TODAY;
    }
    if (daysDiff < 30) {
      return EventTimeframe.LAST_30_DAYS;
    }
    if (daysDiff < 365) {
      return EventTimeframe.LAST_YEAR;
    }
    return EventTimeframe.OLDER;
  };

  const eventsByTimeframe = useMemo(
    () => ({
      [ShopperEventCategory.PURCHASED]: events[
        ShopperEventCategory.PURCHASED
      ].reduce(
        (
          accumulator: Partial<Record<EventTimeframe, BasketCheckoutEvent[]>>,
          event
        ) => {
          const currentEventTimeframe = getEventTimeframe(event);
          accumulator[currentEventTimeframe] = [
            ...(accumulator[currentEventTimeframe] || []),
            event,
          ];
          return accumulator;
        },
        {}
      ),
      [ShopperEventCategory.RECENTLY_VIEWED]: events[
        ShopperEventCategory.RECENTLY_VIEWED
      ].reduce(
        (
          accumulator: Partial<
            Record<
              EventTimeframe,
              (
                | ProductViewShopperEvent
                | CategoryViewShopperEvent
                | SearchShopperEvent
              )[]
            >
          >,
          event
        ) => {
          const currentEventTimeframe = getEventTimeframe(event);
          accumulator[currentEventTimeframe] = [
            ...(accumulator?.[currentEventTimeframe] || []),
            event,
          ];
          return accumulator;
        },
        {}
      ),
    }),
    [events]
  );

  const renderRecentlyViewedProducts = useCallback(() => {
    const recentlyViewedProductsEvents = Object.entries(
      eventsByTimeframe[ShopperEventCategory.RECENTLY_VIEWED] || {}
    );
    if (!recentlyViewedProductsEvents.length) {
      return (
        <ShopperEventsList events={[]} placeholder="No items viewed" large />
      );
    }
    return (
      <Stack gap={3}>
        {recentlyViewedProductsEvents.map(([timeframe, events]) => (
          <ShopperEventsList
            events={events}
            placeholder="No items viewed"
            headerTitle={timeframe}
            large
            key={timeframe}
          />
        ))}
      </Stack>
    );
  }, [eventsByTimeframe]);

  const renderPurchasedItems = useCallback(() => {
    const purchasedItemsEvents = Object.entries(
      eventsByTimeframe[ShopperEventCategory.PURCHASED] || {}
    );
    if (!purchasedItemsEvents.length) {
      return (
        <ShopperEventsList events={[]} placeholder="No past purchases" large />
      );
    }
    return (
      <Stack gap={3}>
        {purchasedItemsEvents.map(([timeframe, events]) => (
          <ShopperEventsList
            events={events}
            placeholder="No past purchases"
            headerTitle={timeframe}
            large
            key={timeframe}
          />
        ))}
      </Stack>
    );
  }, [eventsByTimeframe]);

  const getCategoryTitle = (category: ShopperEventCategory) => {
    if (category === ShopperEventCategory.PURCHASED) {
      return `Purchased items (${events[category].length})`;
    }
    if (category === ShopperEventCategory.RECENTLY_VIEWED) {
      return 'Recently viewed';
    }
    if (category === ShopperEventCategory.BASKET_ADDED_REMOVED) {
      return 'Cart activities';
    }
    return '';
  };

  const handleClose = () => {
    dispatch(chatActions.setSelectedShopperEventCategory(undefined));
  };

  const handleCategoryChange = (category: ShopperEventCategory) => {
    dispatch(chatActions.setSelectedShopperEventCategory(category));
  };

  return (
    <Dialog
      open={Boolean(selectedEventsCategory)}
      onClose={handleClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: (theme) => ({
          height: `calc(100% - ${theme.spacing(4)})`,
        }),
      }}
    >
      <CloseButton onClick={handleClose}>
        <Close />
      </CloseButton>
      <DialogTitle>
        {conversation?.conversationStarter.firstName}’s profile
      </DialogTitle>
      <DialogContent>
        <Stack height="100%" gap={2}>
          <Stack direction="row" gap={2}>
            {Object.values(ShopperEventCategory).map((category) => (
              <Button
                key={category}
                onClick={() => {
                  handleCategoryChange(category);
                }}
                variant={
                  selectedEventsCategory === category ? 'contained' : 'outlined'
                }
              >
                {getCategoryTitle(category)}
              </Button>
            ))}
          </Stack>
          <Stack height="100%" overflow="auto" pt={2}>
            {selectedEventsCategory ===
              ShopperEventCategory.BASKET_ADDED_REMOVED && (
              <Stack gap={4}>
                <ShopperEventsList
                  events={events[selectedEventsCategory].added}
                  placeholder="No items in Cart"
                  headerIcon={IconAddToCart}
                  headerTitle="Added to cart"
                  large
                />
                <ShopperEventsList
                  events={events[selectedEventsCategory].removed}
                  placeholder="No items removed from Cart"
                  headerIcon={IconRemoveFromCart}
                  headerTitle="Removed from cart"
                  large
                />
              </Stack>
            )}
            {selectedEventsCategory === ShopperEventCategory.PURCHASED &&
              renderPurchasedItems()}
            {selectedEventsCategory === ShopperEventCategory.RECENTLY_VIEWED &&
              renderRecentlyViewedProducts()}
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
